/**
 * Rentals SEO Page
 * 
 * Programmatically generated SEO landing page for equipment rentals.
 * Routes: /rentals/{country}, /rentals/{country}/{category}
 * 
 * Features:
 * - Dynamic SEO meta tags
 * - Localized H1/H2/descriptions
 * - Category grid with deep links
 * - Trust badges
 * - Search functionality
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Wrench, MapPin, Search, ArrowRight, Shield, CheckCircle2, Star,
  Package, Truck, HardHat, Hammer, Drill, Ruler
} from 'lucide-react';

// Supported countries
const COUNTRIES = [
  { code: 'FI', slug: 'finland', flag: '🇫🇮', nameKey: 'countries.FI' },
  { code: 'EE', slug: 'estonia', flag: '🇪🇪', nameKey: 'countries.EE' },
  { code: 'SE', slug: 'sweden', flag: '🇸🇪', nameKey: 'countries.SE' },
  { code: 'NO', slug: 'norway', flag: '🇳🇴', nameKey: 'countries.NO' },
  { code: 'DE', slug: 'germany', flag: '🇩🇪', nameKey: 'countries.DE' },
  { code: 'DK', slug: 'denmark', flag: '🇩🇰', nameKey: 'countries.DK' },
];

// Equipment categories
const RENTAL_CATEGORIES = [
  { value: 'construction', icon: '🏗️', labelKey: 'rentals.categories.construction' },
  { value: 'power_tools', icon: '🔧', labelKey: 'rentals.categories.powerTools' },
  { value: 'landscaping', icon: '🌿', labelKey: 'rentals.categories.landscaping' },
  { value: 'lifting', icon: '🏋️', labelKey: 'rentals.categories.lifting' },
  { value: 'cleaning', icon: '🧹', labelKey: 'rentals.categories.cleaning' },
  { value: 'vehicles', icon: '🚚', labelKey: 'rentals.categories.vehicles' },
  { value: 'generators', icon: '⚡', labelKey: 'rentals.categories.generators' },
  { value: 'scaffolding', icon: '🏢', labelKey: 'rentals.categories.scaffolding' },
];

export default function RentalsSeoPage() {
  const params = useParams();
  const location = useLocation();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract country and category from URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const country = params.country || pathParts[1] || '';
  const category = params.category || pathParts[2] || '';

  const normalizedCountry = country?.toLowerCase();
  const normalizedCategory = category?.toLowerCase();
  
  const countryData = COUNTRIES.find(c => c.slug === normalizedCountry);
  const categoryData = RENTAL_CATEGORIES.find(c => c.value === normalizedCategory);

  // Fetch rentals
  useEffect(() => {
    const fetchRentals = async () => {
      setLoading(true);
      try {
        let url = `${process.env.REACT_APP_BACKEND_URL}/api/equipment?`;
        if (countryData) {
          url += `country=${countryData.code}&`;
        }
        if (normalizedCategory) {
          url += `category=${normalizedCategory}&`;
        }
        url += `limit=20`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setRentals(data.equipment || data || []);
        }
      } catch (error) {
        console.error('Error fetching rentals:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRentals();
  }, [normalizedCountry, normalizedCategory, countryData]);

  // Generate SEO content
  const countryName = countryData ? t(countryData.nameKey) : country;
  const categoryName = categoryData ? t(categoryData.labelKey) : null;

  const getSeoContent = () => {
    if (categoryName && countryName) {
      return {
        title: t('rentals.seo.titleFull', { category: categoryName, country: countryName }),
        description: t('rentals.seo.descFull', { category: categoryName, country: countryName }),
        h1: t('rentals.seo.h1Full', { category: categoryName, country: countryName })
      };
    }
    if (countryName) {
      return {
        title: t('rentals.seo.title', { country: countryName }),
        description: t('rentals.seo.desc', { country: countryName }),
        h1: t('rentals.seo.h1', { country: countryName })
      };
    }
    return {
      title: t('rentals.seo.defaultTitle'),
      description: t('rentals.seo.defaultDesc'),
      h1: t('rentals.seo.defaultH1')
    };
  };

  const seo = getSeoContent();
  const canonicalUrl = `https://keikka.com${location.pathname}`;

  // Generate hreflang links
  const hreflangLinks = [
    { lang: 'en', href: `/rentals/${normalizedCountry}${normalizedCategory ? `/${normalizedCategory}` : ''}` },
    { lang: 'et', href: `/rendid/${normalizedCountry}${normalizedCategory ? `/${normalizedCategory}` : ''}` },
    { lang: 'fi', href: `/vuokraus/${normalizedCountry}${normalizedCategory ? `/${normalizedCategory}` : ''}` },
  ];

  return (
    <>
      <Helmet>
        <title>{seo.title} | KEIKKA</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        {hreflangLinks.map(link => (
          <link key={link.lang} rel="alternate" hrefLang={link.lang} href={`https://keikka.com${link.href}`} />
        ))}
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Breadcrumbs */}
        <div className="border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition">{t('common.home')}</Link>
              <span>/</span>
              <Link to="/rentals" className="hover:text-foreground transition">{t('nav.rentals')}</Link>
              {countryData && (
                <>
                  <span>/</span>
                  <Link 
                    to={`/rentals/${normalizedCountry}`} 
                    className={`hover:text-foreground transition flex items-center gap-1 ${!categoryData ? 'text-foreground font-medium' : ''}`}
                  >
                    {countryData.flag} {countryName}
                  </Link>
                </>
              )}
              {categoryData && (
                <>
                  <span>/</span>
                  <span className="text-foreground font-medium">{categoryName}</span>
                </>
              )}
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-8 md:py-12">
          <div className="bg-gradient-to-r from-purple-500/10 to-brand/10 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-2 text-brand mb-4">
              <Package className="h-5 w-5" />
              <span className="font-medium">{countryData?.flag} {countryName}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{seo.h1}</h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl">{seo.description}</p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-600" />
                <span>{t('rentals.seo.verified')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>{t('rentals.seo.insurance')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 text-yellow-500" />
                <span>{t('rentals.seo.ratings')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('rentals.seo.searchPlaceholder')}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button size="lg" onClick={() => navigate('/rentals/new')} data-testid="list-equipment-btn">
                {t('rentals.seo.listEquipment')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Categories Grid (if no category selected) */}
        {!normalizedCategory && (
          <section className="container mx-auto px-4 py-8">
            <h2 className="text-2xl font-bold mb-6">{t('rentals.seo.browseCategories')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
              {RENTAL_CATEGORIES.map((cat) => (
                <Link
                  key={cat.value}
                  to={`/rentals/${normalizedCountry}/${cat.value}`}
                  className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
                  data-testid={`rental-category-${cat.value}`}
                >
                  <span className="text-3xl mb-2">{cat.icon}</span>
                  <span className="text-sm font-medium text-center group-hover:text-brand transition-colors">
                    {t(cat.labelKey)}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Equipment Listings */}
        <section className="container mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold mb-6">
            {categoryName 
              ? t('rentals.seo.availableInCategory', { category: categoryName })
              : t('rentals.seo.availableEquipment')
            }
          </h2>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))}
            </div>
          ) : rentals.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rentals.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/rentals/${item.id}`}
                  className="block"
                  data-testid={`rental-item-${item.id}`}
                >
                  <Card className="h-full hover:border-brand transition-all">
                    <CardHeader>
                      <CardTitle className="line-clamp-2">{item.title || item.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {item.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{item.city || item.location}</span>
                        </div>
                        {item.price_per_day && (
                          <Badge variant="secondary">
                            €{item.price_per_day}/day
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium mb-2">{t('rentals.seo.noEquipment')}</p>
                <p className="text-muted-foreground mb-4">{t('rentals.seo.noEquipmentDesc')}</p>
                <Button onClick={() => navigate('/rentals/new')}>
                  {t('rentals.seo.listYours')}
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* ============================================================
            HUMAN-GRADE SEO CONTENT BLOCKS
            ============================================================ */}
        
        {/* Local Context Block - Country-specific rental information */}
        <section className="container mx-auto px-4 py-8 bg-muted/30 rounded-2xl my-8">
          <h2 className="text-xl font-bold mb-4">
            {t('rentals.seo.rentalMarket', { country: countryName })}
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4">
            <p>{t(`rentals.seo.contentBlocks.intro.${normalizedCountry}`, { defaultValue: t('rentals.seo.contentBlocks.intro.default') })}</p>
            <p>{t(`rentals.seo.contentBlocks.tips.${normalizedCountry}`, { defaultValue: t('rentals.seo.contentBlocks.tips.default') })}</p>
          </div>
        </section>

        {/* Internal Linking - Related Services */}
        <section className="container mx-auto px-4 py-8">
          <h2 className="text-xl font-bold mb-4">{t('rentals.seo.relatedServices')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to={`/jobs/${normalizedCountry}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-jobs"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.jobs')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('rentals.seo.findJobs', { country: countryName })}</p>
            </Link>
            <Link
              to={`/services/${normalizedCountry}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-services"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.services')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('rentals.seo.findServices', { country: countryName })}</p>
            </Link>
            <Link
              to={`/autoremont/${normalizedCountry === 'estonia' ? 'tallinn' : normalizedCountry === 'finland' ? 'helsinki' : 'stockholm'}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-auto"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.autoRepair')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('rentals.seo.vehicleServices')}</p>
            </Link>
          </div>
        </section>

        {/* Other Countries */}
        {countryData && (
          <section className="container mx-auto px-4 py-8 border-t">
            <h2 className="text-xl font-bold mb-6">{t('rentals.seo.otherCountries')}</h2>
            <div className="flex flex-wrap gap-3">
              {COUNTRIES.filter(c => c.slug !== normalizedCountry).map((country) => (
                <Link
                  key={country.code}
                  to={`/rentals/${country.slug}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border hover:border-brand hover:bg-brand/5 transition-all"
                  data-testid={`other-country-${country.slug}`}
                >
                  <span>{country.flag}</span>
                  <span>{t(country.nameKey)}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-12">
          <div className="bg-brand/5 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">{t('rentals.seo.ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">{t('rentals.seo.ctaDesc')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={() => navigate('/rentals/new')}>
                {t('rentals.seo.listNow')}
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/rentals')}>
                {t('rentals.seo.browseAll')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
